import type { Query } from "@rocicorp/zero";
import {
  type ConnectedAppUsage,
  type Schema,
  type WorkflowBranch,
  zql,
} from "./generatedDrizzleSchema";

// The connectors a commit's steps reference. commit_step usages supersede the
// legacy commit_connected_apps table, which no longer has a live write path.
export const commitStepUsages = (
  u: Query<"connectedAppUsages", Schema, ConnectedAppUsage>
) => u.where("type", "commit_step");

const runsAStep = (b: Query<"workflowBranches", Schema, WorkflowBranch>) =>
  b.whereExists("headCommit", (c) =>
    c.whereExists("appUsages", commitStepUsages)
  );

export function liveConnectorUsageQuery(
  usages: (
    u: Query<"connectedAppUsages", Schema, ConnectedAppUsage>
  ) => Query<"connectedAppUsages", Schema, ConnectedAppUsage> = commitStepUsages
) {
  return zql.workflows
    .where("deletedAt", "IS", null)
    .whereExists("branches", runsAStep)
    .related("branches", (b) =>
      runsAStep(b).related("headCommit", (c) => c.related("appUsages", usages))
    );
}

export function liveConnectorUsageBySpaceQuery(
  spaceIds: readonly string[],
  usages?: (
    u: Query<"connectedAppUsages", Schema, ConnectedAppUsage>
  ) => Query<"connectedAppUsages", Schema, ConnectedAppUsage>
) {
  return liveConnectorUsageQuery(usages).where("spaceId", "IN", spaceIds);
}

// The reverse direction: one connector's live usages, with the branch-head
// workflows that run them. The bare exists prunes the superseded commits every
// edit leaves behind; deleted-workflow heads hydrate a null workflow for the
// caller to skip.
export function liveUsagesOfConnectorQuery(connectedAppId: string) {
  return commitStepUsages(zql.connectedAppUsages)
    .where("connectedAppId", connectedAppId)
    .whereExists("branchesAtHead")
    .related("branchesAtHead", (b) =>
      b.related("workflow", (w) => w.where("deletedAt", "IS", null))
    );
}

type LiveUsageWorkflow = {
  id: string;
  spaceId: string;
  branches: readonly {
    headCommit?: {
      appUsages: readonly { connectedAppId: string }[];
    } | null;
  }[];
};

export function liveWorkflowIdsByConnector(
  workflows: readonly LiveUsageWorkflow[]
): Map<string, Set<string>> {
  const byConnector = new Map<string, Set<string>>();
  for (const workflow of workflows)
    for (const branch of workflow.branches)
      for (const { connectedAppId } of branch.headCommit?.appUsages ?? [])
        byConnector.set(
          connectedAppId,
          (byConnector.get(connectedAppId) ?? new Set()).add(workflow.id)
        );
  return byConnector;
}

export function liveConnectorIdsBySpace(
  workflows: readonly LiveUsageWorkflow[]
): Map<string, Set<string>> {
  const bySpace = new Map<string, Set<string>>();
  for (const workflow of workflows) {
    const appIds = bySpace.get(workflow.spaceId) ?? new Set<string>();
    bySpace.set(workflow.spaceId, appIds);
    for (const branch of workflow.branches)
      for (const usage of branch.headCommit?.appUsages ?? [])
        appIds.add(usage.connectedAppId);
  }
  return bySpace;
}
